import { computed } from "vue"
import { useConfigStore } from "@core/stores/config.store"
import type {
    SourceOperator,
    SourceBrand,
    SourceModel,
    OperatorModelsResponse,
    Source,
} from "@types"

/**
 * Composable centralisant l'accès aux données de configuration
 * (variables, opérateurs, marques, modèles) avec lazy loading automatique
 */
export function useSourceInfo() {
    const configStore = useConfigStore()

    // ================================
    // COMPUTED - Lazy loading automatique
    // ================================

    /**
     * Récupère la liste des variables disponibles
     * Charge automatiquement les données si elles ne sont pas encore présentes
     */
    const getVariables = computed(() => {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.variables.length === 0 &&
            !configStore.isLoadingVariables &&
            !configStore.hasErrorVariables
        ) {
            configStore.fetchVariables()
        }
        return configStore.getVariables
    })

    /**
     * Récupère la liste des opérateurs disponibles
     * Charge automatiquement les données si elles ne sont pas encore présentes
     */
    const getOperators = computed(() => {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.operators.length === 0 &&
            !configStore.isLoadingOperators &&
            !configStore.hasErrorOperators
        ) {
            configStore.fetchOperators()
        }
        return configStore.getOperators
    })

    /**
     * Récupère la liste des marques de sources disponibles
     * Charge automatiquement les données si elles ne sont pas encore présentes
     */
    const getSourceBrands = computed(() => {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.sourceBrands.length === 0 &&
            !configStore.isLoadingSourceBrands &&
            !configStore.hasErrorSourceBrands
        ) {
            configStore.fetchSourceBrands()
        }
        return configStore.getSourceBrands
    })

    /**
     * Récupère la liste des modèles de sources disponibles
     * Charge automatiquement les données si elles ne sont pas encore présentes
     */
    const getSourceModels = computed(() => {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.sourceModels.length === 0 &&
            !configStore.isLoadingSourceModels &&
            !configStore.hasErrorSourceModels
        ) {
            configStore.fetchSourceModels()
        }
        return configStore.getSourceModels
    })

    // ================================
    // FONCTIONS HELPER
    // ================================

    /**
     * Récupère un opérateur par son identifiant
     * Charge automatiquement les opérateurs si nécessaire
     * @param idname - Identifiant de l'opérateur
     * @returns L'opérateur correspondant ou undefined si non trouvé
     */
    function getOperator(idname: string): SourceOperator | undefined {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.operators.length === 0 &&
            !configStore.isLoadingOperators &&
            !configStore.hasErrorOperators
        ) {
            configStore.fetchOperators()
        }
        return configStore.getOperatorById(idname)
    }

    /**
     * Récupère une marque de source par son identifiant
     * Charge automatiquement les marques si nécessaire
     * @param idname - Identifiant de la marque
     * @returns La marque correspondante ou undefined si non trouvée
     */
    function getBrand(idname: string): SourceBrand | undefined {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.sourceBrands.length === 0 &&
            !configStore.isLoadingSourceBrands &&
            !configStore.hasErrorSourceBrands
        ) {
            configStore.fetchSourceBrands()
        }
        return configStore.getSourceBrandById(idname)
    }

    /**
     * Récupère un modèle de source par son identifiant
     * Charge automatiquement les modèles si nécessaire
     * @param id - Identifiant du modèle
     * @returns Le modèle correspondant ou undefined si non trouvé
     */
    function getModel(id: string): SourceModel | undefined {
        // Vérifie si les données sont déjà chargées ou en cours de chargement
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            configStore.sourceModels.length === 0 &&
            !configStore.isLoadingSourceModels &&
            !configStore.hasErrorSourceModels
        ) {
            configStore.fetchSourceModels()
        }
        return configStore.getSourceModelById(id)
    }

    /**
     * Récupère les modèles et marques disponibles pour un opérateur donné
     * Charge automatiquement les données si elles ne sont pas en cache
     * @param operator - Identifiant de l'opérateur
     * @returns Les modèles et marques de l'opérateur
     */
    async function getOperatorModels(
        operator: string
    ): Promise<OperatorModelsResponse | undefined> {
        if (!operator) {
            return undefined
        }

        // Vérifie si les données sont déjà en cache
        // Ne relance pas si une erreur s'est déjà produite pour éviter les boucles infinies
        if (
            !configStore.operatorModelsCache[operator] &&
            !configStore.isLoadingOperatorModels &&
            !configStore.hasErrorOperatorModels
        ) {
            await configStore.fetchOperatorModels(operator)
        }

        return configStore.operatorModelsCache[operator]
    }

    /**
     * Détermine le statut wifi d'une source et retourne l'icône et la couleur correspondantes
     * @param source - Source pour laquelle déterminer le statut wifi
     * @returns Objet contenant l'icône et la couleur du statut wifi
     */
    function wifi(source: Source): {
        icon: string
        color: string
        status: "connected" | "disconnected" | "unknown"
    } {
        const GREEN_COLOR = "#69D4B1"
        const GRAY_COLOR = "#E2E9EE"
        const ORANGE_COLOR = "#ffd8bc"
        const RED_COLOR = "#E34A4A"

        const broadcastStatus = (source as any).monitoring?.broadcaststatus

        // Pour les sources IoT et météo, on utilise broadcaststatus
        if (source.type === "iot" || source.type === "meteo") {
            switch (broadcastStatus) {
                case 2:
                    return {
                        icon: "fa-light fa-wifi",
                        color: GREEN_COLOR,
                        status: "connected",
                    }
                case 1:
                    return {
                        icon: "fa-light fa-wifi",
                        color: ORANGE_COLOR,
                        status: "connected",
                    }
                case 0:
                    return {
                        icon: "fa-light fa-wifi-slash",
                        color: RED_COLOR,
                        status: "disconnected",
                    }
                default:
                    return {
                        icon: "fa-light fa-wifi",
                        color: GRAY_COLOR,
                        status: "unknown",
                    }
            }
        }

        // Pour les autres types de sources (pdl, pce, etc.), le wifi n'est pas applicable
        return {
            icon: "pi pi-question-circle",
            color: GRAY_COLOR,
            status: "unknown",
        }
    }

    const isLoadingModelsAndBrands = computed(() => {
        return (
            configStore.isLoadingOperatorModels ||
            configStore.isLoadingSourceBrands ||
            configStore.isLoadingSourceModels
        )
    })

    // ================================
    // RETURN
    // ================================

    return {
        // Computed
        getVariables,
        getOperators,
        getSourceBrands,
        getSourceModels,
        isLoadingModelsAndBrands,

        // Fonctions
        getOperator,
        getBrand,
        getModel,
        getOperatorModels,
        wifi,
    }
}
